import { z } from 'zod';

const objectIdSchema = z
  .string()
  .min(1, 'Required')
  .regex(/^[0-9a-fA-F]{24}$/, 'Invalid Object ID');
const experimentIdSchema = z.string().uuid();

const rulesBaseSchema = z.object({
  experimentId: experimentIdSchema,
  merchantId: objectIdSchema,
  isIncrementality: z.boolean().default(true),
  isOnlyAlbertNetNewUsers: z.boolean().default(false),
  issuerNames: z.array(z.string()).min(1, 'At least one issuer is required'),
  userDateRange: z.object({
    from: z.string().min(1, 'Required'),
    to: z.string().min(1, 'Required'),
  }),
});

const templateFieldsSchema = z.object({
  minEvaluationDurationInMonths: z
    .preprocess(
      Number,
      z.number().int('Must be a whole number').min(0).optional(),
    )
    .transform(Number),
  maxEvaluationDurationInMonths: z
    .preprocess(
      Number,
      z.number().int('Must be a whole number').min(0).optional(),
    )
    .transform(Number),
  transactionCount: z
    .preprocess(Number, z.number().int('Must be a whole number').min(0))
    .transform((value) => Number(value)),
});

const newUsersTemplateFieldsSchema = z.object({});

const activeUsersTemplateFieldsSchema = templateFieldsSchema.pick({
  transactionCount: true,
  minEvaluationDurationInMonths: true,
  maxEvaluationDurationInMonths: true,
});

const lapsedUsersTemplateFieldsSchema = templateFieldsSchema.pick({
  transactionCount: true,
  minEvaluationDurationInMonths: true,
  maxEvaluationDurationInMonths: true,
});

export const offerLinkBaseSchema = z.object({
  offerId: objectIdSchema.min(1, 'Offer ID is required'),
  rules: z
    .discriminatedUnion('templateType', [
      z.object({
        templateType: z.literal('active_users'),
        ...rulesBaseSchema.shape,
        templateFields: activeUsersTemplateFieldsSchema,
      }),
      z.object({
        templateType: z.literal('new_users'),
        ...rulesBaseSchema.shape,
        templateFields: newUsersTemplateFieldsSchema,
      }),
      z.object({
        templateType: z.literal('lapsed_users'),
        ...rulesBaseSchema.shape,
        templateFields: lapsedUsersTemplateFieldsSchema,
      }),
    ])
    // Allow anything to be saved for rules
    .or(z.any()),
});

export const draftOfferLinkSchema = z.object({
  offerId: objectIdSchema.min(1, 'Offer ID is required'),
  status: z.literal('DRAFT').default('DRAFT'),
  experimentId: experimentIdSchema,
  rules: z.any(),
  customSegmentId: z.string().optional(),
});

export const completedOfferLinkSchema = offerLinkBaseSchema.extend({
  status: z.literal('COMPLETED'),
  experimentId: experimentIdSchema,
  customSegmentId: z.string().min(1, 'Required'),
});

export const offerLinkSchema = z.union([
  draftOfferLinkSchema,
  completedOfferLinkSchema,
]);

export type OfferLinkFormValues = z.infer<typeof offerLinkSchema>;
